import { useMemo } from 'react';
import { IAllSuppliers } from 'src/services/useGetOnlineAndOfflineSuppliers';
import { ReviewDocumentResponse } from 'src/services/useReviewDocumentApi';
import { ReviewPoResponse } from 'src/services/useReviewPoApi';
import { IOcrResult } from '../../InvoiceScanManagerContext';
import { CurrentCompanyOcrGenerateDocPermission } from './useDocumentValidationMessage';

interface UseDocumentValidationsProps {
  initialReviewDocValidation: ReviewDocumentResponse;
  reviewDocumentValidations: ReviewDocumentResponse;
  poNumber: IOcrResult;
  reviewPoValidations: ReviewPoResponse;
  docNumber: IOcrResult;
  selectedSupplier: IAllSuppliers;
  isReviewPanelLoading: boolean;
}

interface ValidationResult {
  popUpMessage: string[];
}

const useCreateDocumentValidations = ({
  initialReviewDocValidation,
  reviewDocumentValidations,
  poNumber,
  reviewPoValidations,
  docNumber,
  selectedSupplier,
  isReviewPanelLoading,
}: UseDocumentValidationsProps): ValidationResult => {
  const poNum = poNumber.value;
  const docNum = docNumber.value;
  const permission723 = reviewPoValidations.isPoRequiredToCreateInvoice;
  const { canReceiveInvoicesCompany, canReceiveInvoices } = reviewPoValidations;
  const docMatchStatus = reviewDocumentValidations.matchStatus;
  const poMatchStatus = reviewPoValidations.matchStatus;
  const docMatchSupplier = reviewDocumentValidations.matchedSuppliers?.find(
    (p) => p.supplierId === selectedSupplier.supplierId
  );
  const isExistingOcrDoc =
    CurrentCompanyOcrGenerateDocPermission.includes(docMatchStatus) &&
    (!selectedSupplier.supplierId ||
      (selectedSupplier.supplierId && docMatchSupplier && docMatchSupplier.hasOcrDocument));

  return useMemo(() => {
    const isCreditMemo = initialReviewDocValidation?.documentType === 'Credit Memo';
    const canUseNegativeQty = initialReviewDocValidation?.creditMemoPermissions?.canUseNegativeQty;
    let message = [];

    if (!isReviewPanelLoading) {
      if (isCreditMemo && !canUseNegativeQty) {
        message.push('Permission to create Credit Memos is required.');
      }
      if (
        (poNum.length < 5 && permission723) ||
        (permission723 &&
          (poMatchStatus === 'InvalidMatch' ||
            poMatchStatus === 'MultipleMatch' ||
            poMatchStatus === 'NoMatch'))
      ) {
        message.push('A single, valid PO # is required.');
      }
      if (
        (poMatchStatus === 'ConfirmedMatchCreatedByOtherUser' && !canReceiveInvoicesCompany) ||
        (poMatchStatus === 'ConfirmedMatchCreatedByCurrentUser' && !canReceiveInvoices)
      ) {
        message.push('You do not have permission to create docs for this PO.');
      }
      if (!docNum || isExistingOcrDoc) {
        message.push('A valid doc # is required.');
      }
      if (!selectedSupplier.supplierId) {
        message.push('Supplier is required.');
      }
    }
    return {
      popUpMessage: message,
    };
  }, [
    initialReviewDocValidation?.documentType,
    initialReviewDocValidation?.creditMemoPermissions?.canUseNegativeQty,
    isReviewPanelLoading,
    poNum,
    permission723,
    poMatchStatus,
    canReceiveInvoicesCompany,
    canReceiveInvoices,
    docNum,
    isExistingOcrDoc,
    selectedSupplier.supplierId,
  ]);
};

export default useCreateDocumentValidations;
